// Aggregate view: discrepancy counts over time (per trip, from the trips list)
// and per-detector totals (fetched from each trip report). Bars are plain divs
// so there is no charting dependency.

import { useEffect, useMemo, useState } from "react";
import { getTripReport } from "../api.js";
import type { TripReport, TripSummary } from "../types.js";

interface Props {
  trips: TripSummary[];
}

export function Aggregate({ trips }: Props) {
  const [reports, setReports] = useState<TripReport[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all(trips.map((t) => getTripReport(t.id).catch(() => null)))
      .then((rs) => {
        if (!alive) return;
        setReports(rs.filter((r): r is TripReport => r !== null));
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [trips]);

  const perDetector = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of reports) {
      for (const d of r.discrepancies) {
        counts.set(d.detector, (counts.get(d.detector) ?? 0) + 1);
      }
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [reports]);

  // Discrepancy count per day, from the trips list (no report fetch needed).
  const perDay = useMemo(() => {
    const counts = new Map<string, number>();
    for (const t of trips) {
      const day = t.start.slice(0, 10);
      counts.set(day, (counts.get(day) ?? 0) + t.discrepancyCount);
    }
    return [...counts.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [trips]);

  const maxDetector = Math.max(1, ...perDetector.map(([, n]) => n));
  const maxDay = Math.max(1, ...perDay.map(([, n]) => n));

  return (
    <>
      <div className="panel">
        <h3>Discrepancies over time</h3>
        <div className="bars">
          {perDay.map(([day, n]) => (
            <div className="bar-row" key={day}>
              <span className="muted">{day}</span>
              <div className="bar" style={{ width: `${(n / maxDay) * 100}%` }} />
              <span>{n}</span>
            </div>
          ))}
          {perDay.length === 0 && <p className="muted">No trips.</p>}
        </div>
      </div>

      <div className="panel">
        <h3>Per detector {loading && <span className="muted">(loading…)</span>}</h3>
        <div className="bars">
          {perDetector.map(([detector, n]) => (
            <div className="bar-row" key={detector}>
              <span className="muted">{detector}</span>
              <div className="bar" style={{ width: `${(n / maxDetector) * 100}%` }} />
              <span>{n}</span>
            </div>
          ))}
          {!loading && perDetector.length === 0 && <p className="muted">No discrepancies found.</p>}
        </div>
      </div>
    </>
  );
}
